import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Layers, Lock, Play, Square, X, Repeat, Radio, SkipBack,
  Volume2, VolumeX, HardDrive, FolderOpen, Save, Trash2,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Slider } from '../../../components/ui/slider';
import {
  FREE_SESSION_LIMIT, FREE_TRACK_LIMIT, MAX_TRACKS,
} from '../audio/constants';
import { formatTimeSeconds } from '../audio/audioCodec';

// The "looper console" - one cohesive panel rendered above the
// recordings list. It exposes:
//  - top control bar: play / stop / panic / clear
//  - the multitrack timeline (elapsed / duration)
//  - loop toggle + overdub controls (PRO)
//  - per-track mixer with master fader (PRO)
//  - sessions: save / list / load / delete from IndexedDB
//
// State stays in LooperEngine + useLooperSessions; this component is
// pure presentation. All callbacks come from RecorderPage.

const MultitrackBar = ({
  isPremium,
  recordings,
  // engine snapshot
  selectedTrackIds,
  isMultitrackPlaying,
  multitrackLoading,
  multitrackLoop,
  recMtElapsed,
  recMtDuration,
  masterVolume,
  trackVolumes,
  trackMuted,
  trackSoloed,
  overdubState,
  overdubMultiplier,
  overdubHistory,
  // engine handlers
  onPlayMultitrack,
  onStopMultitrack,
  onPanicStop,
  onClearSelection,
  onSetMultitrackLoop,
  onArmOverdub,
  onCancelOverdub,
  onUndoLastOverdub,
  onSetOverdubMultiplier,
  onToggleMute,
  onToggleSolo,
  onUpdateTrackVolume,
  onUpdateMasterVolume,
  // sessions
  sessions,
  sessionName,
  setSessionName,
  sessionLoading,
  showSessions,
  setShowSessions,
  onSaveSession,
  onLoadSession,
  onDeleteSession,
}) => {
  const { t } = useTranslation();
  const selectedCount = selectedTrackIds.size;

  return (
    <div className="bg-[#121212] border border-purple-500/20 rounded-xl p-4 mb-4" data-testid="multitrack-bar">
      {/* Top control bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Layers className={`w-5 h-5 ${selectedCount > 0 ? 'text-purple-400' : 'text-zinc-600'}`} />
          <span className="text-white text-sm font-medium">
            Multitrack
            {selectedCount > 0 && (
              <span className="text-purple-400 ml-2">
                {selectedCount}/{isPremium ? MAX_TRACKS : FREE_TRACK_LIMIT} selezionate
              </span>
            )}
          </span>
          {!isPremium && (
            <span className="text-xs text-zinc-500 flex items-center gap-1">
              <Lock className="w-3 h-3" /> Max {FREE_TRACK_LIMIT} tracce (PRO: fino a {MAX_TRACKS})
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={onPlayMultitrack}
            disabled={selectedCount === 0 || multitrackLoading}
            size="sm"
            className="bg-purple-500 hover:bg-purple-600 text-white disabled:opacity-40"
            data-testid="multitrack-play-btn"
          >
            {multitrackLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
            ) : (
              <Play className="w-4 h-4 mr-1" />
            )}
            Play Selected
          </Button>
          {isMultitrackPlaying && (
            <Button
              onClick={() => onStopMultitrack()}
              size="sm"
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="multitrack-stop-btn"
            >
              <Square className="w-4 h-4 mr-1 fill-current" /> Stop
            </Button>
          )}
          <Button
            onClick={onPanicStop}
            size="sm"
            className="bg-red-700 hover:bg-red-800 text-white"
            data-testid="panic-stop-btn"
            title={t('recorder.stopAllTitle')}
          >
            <Square className="w-3 h-3 mr-1 fill-current" /> Panic
          </Button>
          {selectedCount > 0 && (
            <Button
              onClick={onClearSelection}
              size="sm"
              variant="ghost"
              className="text-zinc-400 hover:text-white"
              data-testid="multitrack-clear-btn"
            >
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* Multitrack timeline */}
      {(isMultitrackPlaying || recMtDuration > 0) && (
        <div className="flex items-center gap-3 mb-3 mt-3" data-testid="rec-mt-timeline">
          <span className="font-mono text-xs text-zinc-400 w-12">{formatTimeSeconds(recMtElapsed)}</span>
          <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full bg-purple-500 rounded-full transition-[width] duration-100"
              style={{ width: `${recMtDuration > 0 ? (recMtElapsed / recMtDuration * 100) : 0}%` }}
            />
          </div>
          <span className="font-mono text-xs text-zinc-400 w-12 text-right">{formatTimeSeconds(recMtDuration)}</span>
        </div>
      )}

      {/* Loop + Overdub */}
      {selectedCount > 0 && (
        <div className="mt-3 pt-3 border-t border-white/5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => onSetMultitrackLoop(!multitrackLoop)}
                size="sm"
                className={`${
                  multitrackLoop
                    ? 'bg-green-500 hover:bg-green-600 text-white'
                    : 'bg-zinc-700 hover:bg-zinc-600 text-zinc-300'
                }`}
                data-testid="multitrack-loop-toggle"
              >
                <Repeat className="w-4 h-4 mr-1" />
                Loop {multitrackLoop ? 'ON' : 'OFF'}
              </Button>
              {multitrackLoop && recMtDuration > 0 && (
                <span className="text-green-400 text-xs">
                  Durata loop: {formatTimeSeconds(recMtDuration)}
                </span>
              )}
            </div>

            {isPremium && (
              <div className="flex items-center gap-2">
                {overdubState === 'idle' && (
                  <>
                    <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
                      {[1, 2, 4].map((mult) => (
                        <button
                          key={mult}
                          onClick={() => onSetOverdubMultiplier(mult)}
                          className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                            overdubMultiplier === mult
                              ? 'bg-orange-500 text-black'
                              : 'text-zinc-400 hover:text-white'
                          }`}
                          data-testid={`overdub-mult-${mult}`}
                        >
                          {mult}x
                        </button>
                      ))}
                    </div>
                    <Button
                      onClick={onArmOverdub}
                      disabled={!multitrackLoop || !isMultitrackPlaying || selectedCount >= MAX_TRACKS}
                      size="sm"
                      className="bg-orange-500 hover:bg-orange-600 text-black disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="overdub-arm-btn"
                    >
                      <Radio className="w-4 h-4 mr-1" />
                      Arm Overdub
                    </Button>
                    {overdubHistory.length > 0 && (
                      <Button
                        onClick={onUndoLastOverdub}
                        size="sm"
                        className="bg-zinc-600 hover:bg-zinc-500 text-white"
                        data-testid="overdub-undo-btn"
                      >
                        <SkipBack className="w-4 h-4 mr-1" />
                        Undo
                      </Button>
                    )}
                  </>
                )}

                {overdubState === 'armed' && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 bg-orange-500/20 border border-orange-500/50 rounded-lg px-3 py-1.5">
                      <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                      <span className="text-orange-400 text-sm font-medium">
                        Armed ({overdubMultiplier}x) - in attesa...
                      </span>
                    </div>
                    <Button
                      onClick={onCancelOverdub}
                      size="sm"
                      variant="ghost"
                      className="text-zinc-400 hover:text-white"
                      data-testid="overdub-cancel-btn"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                {overdubState === 'recording' && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 bg-red-500/20 border border-red-500/50 rounded-lg px-3 py-1.5">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      <span className="text-red-400 text-sm font-medium">
                        Recording ({overdubMultiplier}x loop)
                      </span>
                    </div>
                    <Button
                      onClick={onCancelOverdub}
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 text-white"
                      data-testid="overdub-stop-btn"
                    >
                      <Square className="w-3 h-3 mr-1 fill-current" />
                      Stop
                    </Button>
                  </div>
                )}

                {overdubState === 'processing' && (
                  <div className="flex items-center gap-2 bg-blue/20 border border-blue/50 rounded-lg px-3 py-1.5">
                    <div className="w-4 h-4 border-2 border-blue border-t-transparent rounded-full animate-spin" />
                    <span className="text-blue text-sm font-medium">
                      Elaborazione...
                    </span>
                  </div>
                )}
              </div>
            )}

            {!isPremium && (
              <div className="flex items-center gap-2 text-zinc-500 text-xs">
                <Lock className="w-3 h-3" />
                <span>Overdub solo PRO</span>
              </div>
            )}
          </div>

          {isPremium && overdubState === 'idle' && (!multitrackLoop || !isMultitrackPlaying) && (
            <div className="mt-2 text-xs text-zinc-500 flex items-center gap-1">
              <Radio className="w-3 h-3" />
              <span>Attiva Loop e premi Play per usare Overdub</span>
            </div>
          )}
        </div>
      )}

      {/* Mixer (PRO, shows during playback) */}
      {isMultitrackPlaying && isPremium && selectedCount > 0 && (
        <div className="mt-4 pt-4 border-t border-white/5 space-y-3" data-testid="mixer-panel">
          <div className="flex items-center gap-3 pb-3 border-b border-white/5" data-testid="master-volume">
            <Volume2 className="w-4 h-4 text-purple-400" />
            <span className="text-purple-400 text-sm font-medium w-20">Master</span>
            <Slider
              value={[masterVolume]}
              onValueChange={(v) => onUpdateMasterVolume(v[0])}
              min={0}
              max={100}
              step={1}
              className="flex-1"
              data-testid="master-vol-slider"
            />
            <span className="text-zinc-400 text-xs font-mono w-10 text-right">{masterVolume}%</span>
          </div>
          {[...selectedTrackIds].map((id, idx) => {
            const rec = recordings.find((r) => r.id === id);
            const isLocalTrack = id.startsWith('local_');
            const trackLabel = rec?.title || (isLocalTrack ? `Loaded Track ${idx + 1}` : `Track ${idx + 1}`);
            const isMuted = !!trackMuted[id];
            const isSoloed = !!trackSoloed[id];
            return (
              <div key={id} className="flex items-center gap-2" data-testid={`mixer-track-${id}`}>
                <span className="text-white text-sm w-24 truncate" title={trackLabel}>
                  {isLocalTrack ? `Loaded ${idx + 1}` : `Track ${idx + 1}`}
                </span>
                <button
                  onClick={() => onToggleMute(id)}
                  className={`w-7 h-7 rounded text-xs font-bold flex items-center justify-center transition-all ${
                    isMuted ? 'bg-red-500 text-white' : 'bg-white/5 text-zinc-500 hover:bg-white/10'
                  }`}
                  title={isMuted ? t('recorder.unmute') : t('recorder.mute')}
                  data-testid={`mute-${id}`}
                >
                  {isMuted ? <VolumeX className="w-3.5 h-3.5" /> : 'M'}
                </button>
                <button
                  onClick={() => onToggleSolo(id)}
                  className={`w-7 h-7 rounded text-xs font-bold flex items-center justify-center transition-all ${
                    isSoloed ? 'bg-amber text-black' : 'bg-white/5 text-zinc-500 hover:bg-white/10'
                  }`}
                  title={isSoloed ? t('recorder.unsolo') : t('recorder.solo')}
                  data-testid={`solo-${id}`}
                >
                  S
                </button>
                <Slider
                  value={[trackVolumes[id] ?? 80]}
                  onValueChange={(v) => onUpdateTrackVolume(id, v[0])}
                  min={0}
                  max={100}
                  step={1}
                  className="flex-1"
                  data-testid={`mixer-vol-${id}`}
                />
                <span className="text-zinc-400 text-xs font-mono w-10 text-right">{trackVolumes[id] ?? 80}%</span>
              </div>
            );
          })}
        </div>
      )}

      {isMultitrackPlaying && !isPremium && (
        <div className="mt-4 pt-4 border-t border-white/5 text-center">
          <div className="flex items-center justify-center gap-2 text-zinc-500 text-sm">
            <Lock className="w-4 h-4" />
            <span>Mixer completo disponibile solo per PRO</span>
          </div>
        </div>
      )}

      {/* Sessions */}
      {selectedCount > 0 && (
        <div className="mt-4 pt-4 border-t border-white/5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <HardDrive className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium">Sessioni Looper</span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowSessions(!showSessions)}
              className="text-zinc-400 hover:text-white"
              data-testid="toggle-sessions-btn"
            >
              <FolderOpen className="w-4 h-4 mr-1" />
              {showSessions ? t('recorder.hide') : t('recorder.show')}
            </Button>
          </div>

          {showSessions && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input
                  value={sessionName}
                  onChange={(e) => setSessionName(e.target.value)}
                  placeholder={t('recorder.sessionNamePlaceholder')}
                  className="flex-1 bg-[#0A0A0A] border-white/10 text-white"
                  data-testid="session-name-input"
                />
                <Button
                  onClick={onSaveSession}
                  disabled={sessionLoading || !sessionName.trim()}
                  size="sm"
                  className="bg-cyan-500 hover:bg-cyan-600 text-black disabled:opacity-40"
                  data-testid="save-session-btn"
                >
                  {sessionLoading ? (
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-1" />
                      Salva
                    </>
                  )}
                </Button>
              </div>

              {!isPremium && (
                <div className="text-xs text-zinc-500 flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  <span>FREE: max {FREE_SESSION_LIMIT} sessioni ({sessions.length}/{FREE_SESSION_LIMIT})</span>
                </div>
              )}

              {sessions.length > 0 && (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      className="bg-white/5 rounded-lg p-2 flex items-center justify-between"
                      data-testid={`session-${session.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{session.name}</p>
                        <p className="text-zinc-500 text-xs">
                          {session.tracks?.length || 0} tracce - {new Date(session.createdAt).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <Button
                          size="sm"
                          onClick={() => onLoadSession(session.id)}
                          disabled={sessionLoading}
                          className="bg-green-500 hover:bg-green-600 text-white h-7 px-2"
                          data-testid={`load-session-${session.id}`}
                        >
                          <Play className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => onDeleteSession(session.id)}
                          disabled={sessionLoading}
                          variant="ghost"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"
                          data-testid={`delete-session-${session.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {sessions.length === 0 && (
                <p className="text-zinc-500 text-xs text-center py-2">
                  Nessuna sessione salvata
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultitrackBar;
